import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Kanban, AlertTriangle, Calendar, DollarSign } from 'lucide-react';
import api from '../api';
import { KanbanColumn, Deal, PipelineStage } from '../types';
import Modal from '../components/Modal';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR');

const isOverdue = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

const PipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Stage change modal
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [stageChangeLoading, setStageChangeLoading] = useState(false);

  const fetchKanban = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/deals/kanban');
      const data = res.data;
      if (Array.isArray(data)) {
        setColumns(data);
        const stagesList = data.map((col: KanbanColumn) => col.stage);
        setStages(stagesList);
      } else if (data.columns) {
        setColumns(data.columns);
        const stagesList = data.columns.map((col: KanbanColumn) => col.stage);
        setStages(stagesList);
      } else {
        setColumns([]);
      }
    } catch (err) {
      setError('Erreur lors du chargement du pipeline.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKanban();
  }, [fetchKanban]);

  const handleDealRightClick = (e: React.MouseEvent, deal: Deal) => {
    e.preventDefault();
    setSelectedDeal(deal);
    setSelectedStageId(deal.stage?.id || '');
    setShowStageModal(true);
  };

  const handleStageChange = async () => {
    if (!selectedDeal || !selectedStageId) return;
    setStageChangeLoading(true);
    try {
      await api.patch(`/deals/${selectedDeal.id}/stage`, { stageId: selectedStageId });
      setShowStageModal(false);
      setSelectedDeal(null);
      fetchKanban();
    } catch (err) {
      // try alternative endpoint
      try {
        await api.patch(`/deals/${selectedDeal.id}`, { stageId: selectedStageId });
        setShowStageModal(false);
        setSelectedDeal(null);
        fetchKanban();
      } catch (err2) {
        // ignore
      }
    } finally {
      setStageChangeLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertTriangle size={48} />
        <p className="empty-state-title">{error}</p>
        <button className="btn" onClick={fetchKanban}>Réessayer</button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><Kanban size={28} /> Pipeline</span>
        </h1>
      </div>

      {columns.length === 0 ? (
        <div className="empty-state">
          <Kanban size={48} />
          <p className="empty-state-title">Aucune étape dans le pipeline</p>
          <p className="empty-state-description">Configurez les étapes du pipeline depuis l'administration.</p>
        </div>
      ) : (
        <div className="kanban-board">
          {columns.map((column) => (
            <div key={column.stage.id} className="kanban-column">
              <div className="kanban-column-header">
                <div
                  className="kanban-column-header-bar"
                  style={{ backgroundColor: column.stage.color || '#6B7280' }}
                />
                <div className="kanban-column-title">{column.stage.name}</div>
                <div className="kanban-column-stats">
                  <span>{column.count} affaire{column.count > 1 ? 's' : ''}</span>
                  <span>{formatCurrency(column.totalValue)}</span>
                </div>
              </div>
              <div className="kanban-column-body">
                {column.deals.map((deal) => (
                  <div
                    key={deal.id}
                    className="kanban-card"
                    onClick={() => navigate(`/deals/${deal.id}`)}
                    onContextMenu={(e) => handleDealRightClick(e, deal)}
                  >
                    <div className="kanban-card-company">
                      {deal.company?.name || 'Sans entreprise'}
                    </div>
                    <div className="kanban-card-title">{deal.title}</div>
                    {deal.estimatedAmount !== undefined && deal.estimatedAmount !== null && (
                      <div className="kanban-card-amount">
                        <DollarSign size={14} style={{ display: 'inline' }} />
                        {formatCurrency(deal.estimatedAmount)}
                      </div>
                    )}
                    <div className="kanban-card-meta">
                      <span>{deal.probability}%</span>
                      {deal.nextStepDueDate && (
                        <span className={isOverdue(deal.nextStepDueDate) ? 'kanban-card-overdue' : ''}>
                          <Calendar size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {formatDate(deal.nextStepDueDate)}
                        </span>
                      )}
                    </div>
                    {deal.nextStepDescription && (
                      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '6px' }}>
                        {deal.nextStepDescription.substring(0, 60)}
                        {deal.nextStepDescription.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </div>
                ))}
                {column.deals.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '0.85rem' }}>
                    Aucune affaire
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        title="Changer l'étape"
        size="sm"
        footer={
          <>
            <button className="btn" onClick={() => setShowStageModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleStageChange} disabled={stageChangeLoading}>
              {stageChangeLoading ? <span className="spinner spinner-sm" /> : 'Confirmer'}
            </button>
          </>
        }
      >
        <p style={{ marginBottom: '12px' }}>
          Déplacer <strong>{selectedDeal?.title}</strong> vers :
        </p>
        <div className="form-group">
          <select
            className="form-select"
            value={selectedStageId}
            onChange={(e) => setSelectedStageId(e.target.value)}
          >
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
        </div>
      </Modal>
    </div>
  );
};

export default PipelinePage;
